import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import FileIcon from "@entrilia/shared/components/FileIcon";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { formatFileSize, getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { fileToUploadValidator } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../../api/adminApi";
import type { UploadWorkflowAttachmentResult } from "../../../../../api/types/workflowTypes";

interface Props {
  workflowId: string;
  onboard: boolean;
  existingFileNames: string[];
  onClose: () => void;
  onUploaded: (result: UploadWorkflowAttachmentResult) => void;
}

const acceptedFileExtensions = [".pdf", ".doc", ".docx"];
const maxFileSize = 25_000_000;

const validateFile = fileToUploadValidator({ maxFileSize, acceptedFileExtensions });

const uploadWorkflowAttachment = withErrorHandling(adminApi.uploadWorkflowAttachment);

const UploadWorkflowAttachmentDialog = ({ workflowId, onboard, existingFileNames, onClose, onUploaded }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState<string>();

  const handleAddFiles = (files: File[]) => {
    const addedFile = files[0];
    if (!addedFile) {
      return;
    }

    const validationResult = validateFile(addedFile);
    setFileError(validationResult.error);
    setFile(addedFile);
  };

  const handleDeleteFile = () => {
    setFile(undefined);
    setFileError(undefined);
  };

  const handleUpload = async () => {
    if (file === undefined) {
      return;
    }

    setUploading(true);
    const [result, error] = await uploadWorkflowAttachment(workflowId, file, { onboard });
    setUploading(false);

    if (error) {
      logError(error, "[UploadWorkflowAttachmentDialog] uploadWorkflowAttachment");
      sendNotificationError(error.message);
      return;
    }

    onUploaded(result);
    onClose();
  };

  const willReplaceExistingFile =
    file !== undefined &&
    !uploading &&
    existingFileNames.some((existingFileName) => existingFileName.toLowerCase() === file.name.toLowerCase());

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>Upload File</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent>
        <Stack spacing={3} sx={{ height: "16rem" }}>
          {file === undefined && (
            <FilesDropArea
              acceptedFileExtensions={acceptedFileExtensions}
              subtitle="Supported formats: pdf, doc, docx."
              onFilesAdd={handleAddFiles}
              containerProps={{ sx: { flex: 1 } }}
            />
          )}

          {file !== undefined && (
            <Stack spacing={1}>
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "hidden" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell width={80}>Size</TableCell>
                      <TableCell width={56}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          {willReplaceExistingFile && (
                            <Tooltip title="A file with this name already exists in this workflow. Uploading this file will replace the existing file.">
                              <ErrorRoundedIcon color="warning" />
                            </Tooltip>
                          )}
                          <FileIcon fileExtension={getFileExtension(file.name)} />
                          <Typography sx={{ wordBreak: "break-word" }}>{file.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography noWrap sx={{ width: 64 }}>
                          {formatFileSize(file.size)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={handleDeleteFile} disabled={uploading}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {willReplaceExistingFile && (
                <FormHelperText sx={(t) => ({ color: t.palette.warning.main })}>
                  A file with this name already exists in the workflow. Uploading it will replace the existing file.
                </FormHelperText>
              )}
            </Stack>
          )}

          {!!fileError && <FormHelperText error>{fileError}</FormHelperText>}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={onClose} cancelDisabled={uploading}>
        <Button
          variant="contained"
          loading={uploading}
          onClick={handleUpload}
          disabled={file === undefined || !!fileError}
        >
          Upload
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default UploadWorkflowAttachmentDialog;
